import { FormikFormField } from '@hs-baumappe/forms';
import {
  Box,
  FlexGrid,
  FlexGridColumn,
  Stack,
  uniqueId,
  VisuallyHidden,
} from '@hs-baumappe/legacy-ui';
import { useField, useFormikContext } from 'formik';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AIButtonWrapper } from '@hs-baumappe/llm-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import RichTextEditor from '../../../RichTextEditor';
import { OvertimeFormValues } from '../../form';

import { LaunchDarklyFlagSet } from '../../../../launchdarkly/launchDarklyFlagSet';

interface WorkInformationSectionProps {
  pdfRedesignEnabled: boolean;
}

export default function WorkInformationSection({
  pdfRedesignEnabled,
}: WorkInformationSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { values } = useFormikContext<OvertimeFormValues>();
  const workInformationTitleId = useRef(uniqueId('work-information-title-'));
  const [workDescriptionField, , workDescriptionFieldHelpers] =
    useField<OvertimeFormValues['workDescription']>('workDescription');
  const [imagesField] = useField<OvertimeFormValues['images']>('images');

  const images = imagesField.value.reduce<{ url: string; thumbnailUrl: string; label: string }[]>(
    (result, image) => {
      if (!image.imageUrl) {
        return result;
      }

      return [
        ...result,
        {
          url: image.imageUrl,
          thumbnailUrl: image.imageThumbnailUrl ?? image.imageUrl,
          label: image.label ?? '',
        },
      ];
    },
    [],
  );

  const { overtimeRephraseWithAi, rephraseWithImages } = useFlags<LaunchDarklyFlagSet>();

  return (
    <Stack gap="medium" role="group" aria-labelledby={workInformationTitleId.current}>
      {/* This section does not have title for some reason. Added visually hidden label for accessibility. */}
      <VisuallyHidden id={workInformationTitleId.current}>
        {t('overtimeForm.constructionProject.title')}
      </VisuallyHidden>

      <FlexGrid>
        <FlexGridColumn sm={6}>
          <FormikFormField
            name="workName"
            label={t('overtimeForm.constructionProject.name.label')}
            placeholder={t('overtimeForm.constructionProject.name.placeholder')}
          />
        </FlexGridColumn>
      </FlexGrid>
      <Box>
        <AIButtonWrapper
          enabled={overtimeRephraseWithAi}
          trades={values.professions ?? []}
          availableImages={images}
          value={workDescriptionField.value}
          context="OVERTIME"
          setValue={(value) => {
            workDescriptionFieldHelpers.setValue(value);
          }}
          flags={{ imagesEnabled: rephraseWithImages }}
          ui="v1"
          previewStyle={{ position: 'relative' }}
        >
          <FormikFormField<typeof RichTextEditor>
            control={RichTextEditor}
            name="workDescription"
            label={t(
              'overtimeForm.constructionProject.description.label',
              !pdfRedesignEnabled ? { context: 'legacy' } : undefined,
            )}
            onChange={(value) => workDescriptionFieldHelpers.setValue(value)}
            imageUploadingDisabled
          />
        </AIButtonWrapper>
      </Box>
    </Stack>
  );
}
